import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  linkifyText,
  segmentQuotedLines,
  splitTextQuote,
  type ForwardHeaders,
} from '@/lib/inbox/email-quotes-text'
import { ForwardedHeader } from './forwarded-header'

/** "⋯ Visa citerad text", in both views: a real button, under the new text. */
export function QuoteToggle({ open, onToggle }: { open: boolean; onToggle: () => void }) {
  const { t } = useTranslation('inbox')
  return (
    <button
      type="button"
      data-slot="quote-toggle"
      aria-expanded={open}
      onClick={onToggle}
      className="text-muted-foreground hover:text-foreground hover:bg-muted/50 inline-flex min-h-8 items-center gap-1.5 rounded-md px-2 text-xs transition-colors"
    >
      <span aria-hidden className="font-semibold tracking-wider">
        ⋯
      </span>
      {t(open ? 'body.hideQuoted' : 'body.showQuoted')}
    </button>
  )
}

/** Text with its URLs as links. React text nodes only — never innerHTML. */
function LinkifiedText({ text }: { text: string }) {
  return linkifyText(text).map((token, index) =>
    token.type === 'link' ? (
      <a
        key={index}
        href={token.href}
        target="_blank"
        rel="noopener noreferrer"
        className="text-blue-600 underline underline-offset-2 dark:text-blue-400"
      >
        {token.text}
      </a>
    ) : (
      token.text
    )
  )
}

function QuoteBlock({ children }: { children: React.ReactNode }) {
  return (
    <blockquote className="text-muted-foreground border-border border-l-2 pl-3">
      {children}
    </blockquote>
  )
}

/** Plain text with its `>` lines as (nested) quote blocks. */
function TextBlocks({ text }: { text: string }) {
  return segmentQuotedLines(text).map((segment, index) =>
    segment.quote ? (
      <QuoteBlock key={index}>
        <TextBlocks text={segment.text} />
      </QuoteBlock>
    ) : (
      segment.text && (
        <div key={index} className="whitespace-pre-wrap break-words">
          <LinkifiedText text={segment.text} />
        </div>
      )
    )
  )
}

function ForwardedMessage({ headers, text }: { headers?: ForwardHeaders; text: string }) {
  const { t } = useTranslation('inbox')
  return (
    <section data-slot="forwarded-message" className="rounded-md border px-3 py-2.5">
      <h4 className="text-muted-foreground mb-2 text-xs font-medium">{t('body.forwarded')}</h4>
      {headers && <ForwardedHeader headers={headers} />}
      <TextBlocks text={text} />
    </section>
  )
}

/**
 * The text view of an email (board eeqo): URLs are links, a reply's history is
 * collapsed behind a toggle, a forward is framed with its headers and stays
 * open — the forwarded message is the point of the mail.
 */
export function EmailTextBody({ text, subject }: { text: string; subject: string | null }) {
  const { t } = useTranslation('inbox')
  const split = useMemo(() => splitTextQuote(text, subject), [text, subject])
  const [showQuoted, setShowQuoted] = useState(false)
  // A reply of nothing but history must not open as an empty page.
  const quoteOpen = showQuoted || !split.body
  return (
    <div
      data-slot="inbox-email-body"
      className="space-y-3 text-sm leading-relaxed"
      data-quote-kind={split.kind}
    >
      {split.body ? (
        <TextBlocks text={split.body} />
      ) : (
        split.kind === 'none' && <p>{t('detail.emptyBody')}</p>
      )}
      {split.kind === 'forward' && <ForwardedMessage headers={split.headers} text={split.quoted} />}
      {split.kind === 'reply' && (
        <>
          {split.body && <QuoteToggle open={quoteOpen} onToggle={() => setShowQuoted((v) => !v)} />}
          {quoteOpen && (
            <QuoteBlock>
              <TextBlocks text={split.quoted} />
            </QuoteBlock>
          )}
        </>
      )}
    </div>
  )
}
